<script setup>
import router from '@/router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { ref } from 'vue'
import { useDisplay } from 'vuetify'
import { viteConfig } from '@/services/utils'

const { locale } = useI18n()
const userStore = useUserStore()
const { mobile } = useDisplay()
const drawer = ref(false)

const changeLanguage = (lang) => {
  locale.value = lang
  userStore.setLanguage(lang)
}

const handleLogout = async () => {
  await userStore.logout()
  drawer.value = false
  router.push({ name: 'home' })
}

const closeDrawer = () => {
  drawer.value = false
}
</script>

<template>
  <!-- Navigation Drawer for Mobile -->
  <v-navigation-drawer
    v-model="drawer"
    location="left"
    temporary
    v-if="mobile"
  >
    <v-list>
      <!-- Main Navigation Items -->
      <v-list-item 
        prepend-icon="mdi-home" 
        :title="$t('navigation.home')"
        to="/"
        @click="closeDrawer"
      />
      <v-list-item 
        v-if="userStore.isAuthenticated"
        prepend-icon="mdi-finance" 
        :title="$t('myBets.title')"
        to="/mybets"
        @click="closeDrawer"
      />
      <v-list-item 
        prepend-icon="mdi-calendar-multiple" 
        :title="$t('events.list.title')"
        to="/list"
        @click="closeDrawer"
      />
      <v-list-item 
        prepend-icon="mdi-trophy" 
        :title="$t('navigation.standings')"
        to="/standings"
        @click="closeDrawer"
      />
      <v-list-item 
        prepend-icon="mdi-information-outline" 
        :title="$t('navigation.info')"
        to="/info"
        @click="closeDrawer"
      />
      
      <v-divider />
      
      <!-- Authentication Section for Mobile -->
      <template v-if="!userStore.isAuthenticated">
        <v-list-item 
          prepend-icon="mdi-login" 
          :title="$t('common.login')"
          to="/login"
          @click="closeDrawer"
        />
        <v-list-item 
          prepend-icon="mdi-account-plus" 
          :title="$t('common.register')"
          to="/register"
          @click="closeDrawer"
        />
      </template>
      
      <!-- User Menu Items for Mobile when authenticated -->
      <template v-else>
        <v-list-subheader>{{ userStore.userName }}</v-list-subheader>
        <v-list-item 
          prepend-icon="mdi-account" 
          :title="$t('navigation.profile')" 
          to="/profile"
          @click="closeDrawer"
        />
        <v-list-item 
          prepend-icon="mdi-cog" 
          :title="$t('navigation.settings')" 
          to="/settings"
          @click="closeDrawer"
        />
        <v-list-item 
          prepend-icon="mdi-logout" 
          :title="$t('common.logout')"
          @click="handleLogout"
        />
      </template>
      
      <v-divider />
      
      <!-- Language Selection for Mobile -->
      <v-list-subheader>{{ $t('navigation.language') }}</v-list-subheader>
      <v-list-item 
        prepend-icon="mdi-translate"
        :title="`English`"
        @click="changeLanguage('en')"
        :class="{ 'v-list-item--active': locale === 'en' }"
      />
      <v-list-item 
        prepend-icon="mdi-translate"
        :title="`Italiano`"
        @click="changeLanguage('it')"
        :class="{ 'v-list-item--active': locale === 'it' }"
      />
    </v-list>
  </v-navigation-drawer>

  <v-app-bar>
    <!-- Mobile Menu Button -->
    <v-app-bar-nav-icon
      v-if="mobile"
      @click="drawer = !drawer"
    />
    
    <!-- Desktop Navigation -->
    <template v-if="!mobile">
      <v-btn to="/" variant="text">{{ $t('navigation.home') }}</v-btn>
      <v-btn v-if="userStore.isAuthenticated" to="/mybets" variant="text">{{ $t('myBets.title') }}</v-btn>
      <v-btn to="/list" variant="text">{{ $t('events.list.title') }}</v-btn>
      <v-btn to="/standings" variant="text">{{ $t('navigation.standings') }}</v-btn>
      <v-btn to="/info" variant="text">{{ $t('navigation.info') }}</v-btn>
      <v-btn v-if="userStore.hasCombo()" to="/combo" variant="text" color="amber" :title="$t('combo.newCombo')"><v-icon>mdi-shape-outline</v-icon></v-btn>
    </template>
    
    <!-- App Title for Mobile -->
    <v-toolbar-title v-if="mobile" class="ml-2">
      {{ viteConfig('APP_NAME') }}
    </v-toolbar-title>
    
    <v-spacer></v-spacer>

    <!-- Desktop Authentication Section -->
    <template v-if="!mobile">
      <div v-if="!userStore.isAuthenticated" class="d-flex align-center ga-2 mr-4">
        <v-btn 
          to="/login"
          variant="text"
          size="small"
          prepend-icon="mdi-login"
        >
          {{ $t('common.login') }}
        </v-btn>
        <v-btn 
          to="/register"
          variant="outlined"
          size="small"
          color="primary"
          prepend-icon="mdi-account-plus"
        >
          {{ $t('common.register') }}
        </v-btn>
      </div>

      <!-- User Menu when authenticated (Desktop) -->
      <div v-else class="d-flex align-center ga-2 mr-4">
        <v-menu location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              prepend-icon="mdi-account-circle"
              append-icon="mdi-chevron-down"
            >
              {{ userStore.userName }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item 
              prepend-icon="mdi-finance" 
              :title="$t('myBets.title')"
              to="/mybets"
            />
            <v-list-item 
              prepend-icon="mdi-account" 
              :title="$t('navigation.profile')" 
              to="/profile"
            />
            <v-list-item 
              prepend-icon="mdi-cog" 
              :title="$t('navigation.settings')" 
              to="/settings"
            />
            <v-divider />
            <v-list-item 
              prepend-icon="mdi-logout" 
              :title="$t('common.logout')"
              @click="handleLogout"
            />
          </v-list>
        </v-menu>
      </div>
      
      <!-- Language Selector (Desktop) -->
      <v-btn-group>
        <v-btn 
          @click="changeLanguage('en')" 
          :variant="locale === 'en' ? 'tonal' : 'text'"
          size="small"
        >
          EN
        </v-btn>
        <v-btn 
          @click="changeLanguage('it')" 
          :variant="locale === 'it' ? 'tonal' : 'text'"
          size="small"
        >
          IT
        </v-btn>
      </v-btn-group>
    </template>
    
    <!-- Mobile Language Quick Switch -->
    <template v-else>
      <v-btn v-if="userStore.hasCombo()" to="/combo" variant="text" color="amber"><v-icon>mdi-shape-outline</v-icon></v-btn>
      
      <v-menu location="bottom end">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            size="small"
            icon="mdi-translate"
          />
        </template>
        <v-list>
          <v-list-item 
            :title="`English`"
            @click="changeLanguage('en')"
            :class="{ 'v-list-item--active': locale === 'en' }"
          />
          <v-list-item 
            :title="`Italiano`"
            @click="changeLanguage('it')"
            :class="{ 'v-list-item--active': locale === 'it' }"
          />
        </v-list>
      </v-menu>
    </template>
  </v-app-bar>
</template>